import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { PrimeVue } from '@primevue/core';
import { ConfirmationService, ToastService } from 'primevue';
import Tooltip from 'primevue/tooltip';
import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query';

import './app/style/style.css';
import App from '@/App.vue';
import router from '@/app/routes/router';
import i18n from '@/shared/i18n/i18n';
import idAllTheme from '@/app/style/theme/primevue/theme';
import idallPassthrough from '@/app/style/theme/primevue/passthrough';
import { useAuthentication } from '@/shared/composables/useAuthentication';
import { fetchImpersonationToken } from '@/shared/api/impersonation';

const { requireUserLogin, startImpersonation } = useAuthentication();

/**
 * Une session ouverte depuis la console d'administration, s'il y en a une.
 *
 * Le code arrive dans le fragment (`#_imp=…`) et non dans un paramètre de requête : un
 * fragment ne part ni dans les journaux du serveur ni dans le `Referer`. Il est retiré de la
 * barre d'adresse — un code déjà consommé ne sert plus à rien, mais il n'a aucune raison de
 * rester lisible par-dessus l'épaule.
 *
 * Rend `true` quand l'application doit démarrer sur cette session plutôt que d'envoyer la
 * personne se connecter.
 */
/**
 * Retire `#_imp=…` de la barre d'adresse.
 *
 * Appelé deux fois, et il le faut : `createWebHistory()` lit l'adresse courante — fragment
 * compris — au chargement du module, c'est-à-dire avant que ce fichier n'exécute quoi que
 * ce soit, puis la réécrit telle quelle à sa première navigation. Un seul nettoyage, au
 * démarrage, est donc défait par le routeur une fraction de seconde plus tard.
 */
function stripImpersonationFragment(): void {
  if (!window.location.hash.startsWith('#_imp=')) return;
  // `window.history.state` et non `{}`, pour la même raison qu'à côté : Vue Router y range
  // sa position de défilement et de quoi revenir en arrière.
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + window.location.search
  );
}

async function bootstrapImpersonation(): Promise<boolean> {
  const hash = window.location.hash;
  if (!hash.startsWith('#_imp=')) return false;

  let code: string;
  try {
    code = decodeURIComponent(hash.slice('#_imp='.length));
  } catch {
    return false;
  }
  if (!code) return false;

  stripImpersonationFragment();

  try {
    const { accessToken, targetEmail } = await fetchImpersonationToken(code);
    // Trois segments, sinon ce n'est pas un JWT et l'API le refusera de toute façon —
    // autant repartir sur la connexion normale que monter une application muette.
    if (accessToken.split('.').length !== 3) {
      console.error("[impersonation] le jeton reçu n'est pas un JWT");
      return false;
    }
    startImpersonation(accessToken, targetEmail);
    return true;
  } catch (error) {
    console.error('[impersonation] échange du code impossible', error);
    return false;
  }
}

function renderApp() {
  const app = createApp(App);

  app.use(createPinia());
  app.use(router);
  app.use(i18n);

  app.use(VueQueryPlugin, {
    queryClient: new QueryClient({
      defaultOptions: {
        queries: { refetchOnWindowFocus: false, retry: false }
      }
    })
  });

  app.use(PrimeVue, {
    pt: idallPassthrough,
    theme: {
      preset: idAllTheme,
      options: {
        // The application has no dark mode: pinning the selector to a class no
        // element carries keeps PrimeVue from following the OS preference.
        darkModeSelector: 'light',
        cssLayer: {
          name: 'primevue',
          order: 'tailwind-base, primevue, tailwind-utilities'
        }
      }
    }
  });

  app.use(ToastService);
  app.use(ConfirmationService);
  app.directive('tooltip', Tooltip);

  app.mount('#app');

  // Après le routeur, qui restaure l'adresse qu'il avait lue au chargement du module.
  void router.isReady().then(stripImpersonationFragment);
}

// Nothing renders before Keycloak has answered: every screen needs an identity,
// so an anonymous first paint would only be a flash of an empty shell. A session opened
// from the console already carries its identity, so it skips the login entirely.
void bootstrapImpersonation().then((impersonating) => {
  if (impersonating) renderApp();
  else void requireUserLogin(renderApp);
});
